import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import emailjs from '@emailjs/browser';
import { Helmet } from 'react-helmet-async';
import { FaInstagram, FaFacebookF } from 'react-icons/fa';
import { HiOutlineMailOpen } from 'react-icons/hi';
import { FiMapPin } from 'react-icons/fi';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FranchiseForm from '../components/FranchiseForm';

const Contact: React.FC = () => {
  const { t } = useTranslation();

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    address: '',
    phone: '',
    reason: '',
    message: '',
    agree: false
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setValidationErrors({});

    try {
      // EmailJS configuration
      const serviceId = 'service_fsojiud';
      const templateId = 'template_xdv2w46'; // TODO: Replace with your actual Contact Template ID
      const publicKey = 'uTLjmEPZsOW67O6OZ'; // TODO: Replace with your actual Public Key

      const templateParams = {
        to_name: 'Owner',
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        address: formData.address,
        reason: formData.reason,
        message: formData.message,
      };

      await emailjs.send(serviceId, templateId, templateParams, publicKey);

      // Success - show message and reset form
      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        address: '',
        phone: '',
        reason: '',
        message: '',
        agree: false
      });

      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setSubmitted(false);
      }, 5000);

      if (process.env.NODE_ENV !== 'production') console.log('✅ Contact form submitted successfully');
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error('❌ Error submitting contact form:', err);
      setError('Failed to send message. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Helmet>
        <title>Contact Us | Aswad Amruttulya - Get in Touch</title>
        <meta name="description" content="Contact Aswad Amruttulya for franchise inquiries, feedback, or support. Reach us via our contact form, email, or social media." />
        <meta property="og:title" content="Contact Us | Aswad Amruttulya" />
        <meta property="og:description" content="Have questions about our tea or franchise opportunities? Get in touch with the Aswad Amruttulya team." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://aswadamruttulya.com/contact" />
      </Helmet>
      <Header />
      <section className="page-section contact-page">
        <div className="container">
          <div className="page-hero-banner">
            <span className="page-badge">{t('contact_page_heading')}</span>
            <h1 className="page-hero-title">{t('contact_page_title')}</h1>
            <p className="page-hero-desc">{t('contact_page_subtitle')}</p>
          </div>
          <div className="grid grid-2 contact-page-grid">
            <div className="card contact-info-card">
              <h3>{t('reach_us')}</h3>
              <div className="contact-info-list">
                <div className="contact-info-item">
                  <span className="contact-info-icon location-icon"><FiMapPin /></span>
                  <div className="contact-info-text">
                    <span className="contact-info-label">{t('contact_address_label')}</span>
                    <span>{t('contact_address_text')}</span>
                  </div>
                </div>
                <div className="contact-info-item">
                  <span className="contact-info-icon email-icon"><HiOutlineMailOpen /></span>
                  <div className="contact-info-text">
                    <span className="contact-info-label">{t('contact_email_label')}</span>
                    <a href="mailto:aswadamruttuly@gmail.com">{t('contact_email_value')}</a>
                  </div>
                </div>
                <div className="contact-info-item">
                  <span className="contact-info-icon instagram-icon"><FaInstagram /></span>
                  <div className="contact-info-text">
                    <span className="contact-info-label">{t('contact_instagram_label')}</span>
                    <a href="https://www.instagram.com/aswad_amruttuly" target="_blank" rel="noopener noreferrer">{t('follow_on_instagram')}</a>
                  </div>
                </div>
                <div className="contact-info-item">
                  <span className="contact-info-icon facebook-icon"><FaFacebookF /></span>
                  <div className="contact-info-text">
                    <span className="contact-info-label">{t('contact_facebook_label')}</span>
                    <a href="https://www.facebook.com/share/14YMmoeMZ8D/" target="_blank" rel="noopener noreferrer">{t('follow_on_facebook')}</a>
                  </div>
                </div>
              </div>
            </div>
            <div className="card contact-map-card">
              <iframe
                src="https://maps.google.com/maps?q=Jijamata+Chowk,+Talegaon+Dabhade,+Pune&t=&z=15&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="300"
                style={{ border: 0, borderRadius: '12px' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={t('map_title')}
              ></iframe>
            </div>
          </div>

          <div className="contact-form-section">
            <h3>{t('get_in_touch_title')}</h3>
            <p>{t('get_in_touch_subtitle')}</p>

            {submitted && (
              <div className="success-message" style={{
                padding: '15px 20px',
                marginBottom: '20px',
                backgroundColor: '#4CAF50',
                color: 'white',
                borderRadius: '8px',
                animation: 'fadeIn 0.5s ease-in'
              }}>
                <p>✅ {t('thank_you_message')}</p>
              </div>
            )}

            {error && (
              <div className="error-message" style={{
                padding: '15px 20px',
                marginBottom: '20px',
                backgroundColor: '#f44336',
                color: 'white',
                borderRadius: '8px',
                animation: 'fadeIn 0.5s ease-in'
              }}>
                <p>❌ {error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">{t('full_name_label')}</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={loading}
                    className={validationErrors.name ? 'input-error' : ''}
                  />
                  {validationErrors.name && (
                    <span className="error-text" style={{ color: '#f44336', fontSize: '12px' }}>
                      {validationErrors.name}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="email">{t('email_address_label')}</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={loading}
                    className={validationErrors.email ? 'input-error' : ''}
                  />
                  {validationErrors.email && (
                    <span className="error-text" style={{ color: '#f44336', fontSize: '12px' }}>
                      {validationErrors.email}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">{t('contact_number_label')}</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="10-digit number"
                    className={validationErrors.phone ? 'input-error' : ''}
                  />
                  {validationErrors.phone && (
                    <span className="error-text" style={{ color: '#f44336', fontSize: '12px' }}>
                      {validationErrors.phone}
                    </span>
                  )}
                </div>

                {/* IMPROVED: Fixed placeholder text from "optional_label" to clear, descriptive text */}
                <div className="form-group">
                  <label htmlFor="address">{t('home_address_label')}</label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Enter your address (optional)"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="reason">{t('reason_for_contact_label')}</label>
                <select
                  id="reason"
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  disabled={loading}
                  className={validationErrors.reason ? 'input-error' : ''}
                >
                  <option value="">{t('select_reason_option')}</option>
                  <option value="general">{t('reason_general')}</option>
                  <option value="order">{t('reason_order')}</option>
                  <option value="franchise">{t('reason_franchise')}</option>
                  <option value="feedback">{t('reason_feedback')}</option>
                  <option value="support">{t('reason_support')}</option>
                  <option value="other">{t('reason_other')}</option>
                </select>
                {validationErrors.reason && (
                  <span className="error-text" style={{ color: '#f44336', fontSize: '12px' }}>
                    {validationErrors.reason}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="message">Message (Optional)</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="Additional details..."
                  rows={5}
                  style={{ fontFamily: 'inherit', resize: 'vertical' }}
                />
              </div>

              <div className="form-group checkbox-group">
                <input
                  type="checkbox"
                  id="agree"
                  name="agree"
                  checked={formData.agree}
                  onChange={handleChange}
                  disabled={loading}
                />
                <label htmlFor="agree">
                  {t('agree_policy_text')}
                </label>
                {validationErrors.agree && (
                  <span className="error-text" style={{ color: '#f44336', fontSize: '12px', display: 'block' }}>
                    {validationErrors.agree}
                  </span>
                )}
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
                style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
              >
                {loading ? '⏳ Submitting...' : t('send_message')}
              </button>
            </form>
          </div>
        </div>
      </section>
      <FranchiseForm />
      <Footer />
    </div>
  );
};

export default Contact;